'use client'

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { UserAvatar } from '@/components/partials'
import { cn, formatNumber, getResizedPictureUrl } from '@/lib/utils'
import { formatDateTime } from '@/lib/date-utils'
import {
  useDashboard,
  useNewUsersChart,
  useNewOrdersChart,
  useRevenueChart,
  useOrderStatusChart,
  useCurrencies
} from '@/hooks'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import {
  BellIcon,
  BriefcaseIcon,
  CalendarClockIcon,
  CalendarDaysIcon,
  CalendarIcon,
  FileClockIcon,
  FileIcon,
  FileTextIcon,
  FolderTreeIcon,
  ImagesIcon,
  LanguagesIcon,
  LayersIcon,
  MailIcon,
  NewspaperIcon,
  PackageIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SlidersHorizontalIcon,
  TagIcon,
  TrendingUpIcon,
  UserIcon,
  UsersIcon,
  VideoIcon,
  ArrowRightLeftIcon
} from 'lucide-react'

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6']

type ChartRange = '7d' | '30d' | '90d' | '12m'

const RangeSelect = ({
  value,
  onChange,
  t
}: {
  value: ChartRange
  onChange: (v: ChartRange) => void
  t: (key: string) => string
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-32 text-xs">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {(['7d', '30d', '90d', '12m'] as const).map(r => (
        <SelectItem key={r} value={r} className="text-xs">
          {t(`chart_range_${r}`)}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
)

const Dashboard = () => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canViewUsers = hasPermission(currentUser, 'USERS', 'LIST')
  const canViewOrders = hasPermission(currentUser, 'ORDERS', 'LIST')
  const canViewCurrencies = hasPermission(currentUser, 'CURRENCIES', 'LIST')
  const { data, isLoading, error, refetch, isRefetching } = useDashboard()
  const queryClient = useQueryClient()
  const handleRefresh = async () => {
    await refetch()
    await queryClient.invalidateQueries({ queryKey: ['dashboard-charts'] })
    await refetchCurrencies()
  }
  const [newUsersRange, setNewUsersRange] = useState<'7d' | '30d' | '90d' | '12m'>('30d')
  const [newOrdersRange, setNewOrdersRange] = useState<'7d' | '30d' | '90d' | '12m'>('30d')
  const [revenueRange, setRevenueRange] = useState<'7d' | '30d' | '90d' | '12m'>('30d')
  const [orderStatusRange, setOrderStatusRange] = useState<'7d' | '30d' | '90d' | '12m'>('30d')
  const { data: newUsersData, isLoading: newUsersLoading } = useNewUsersChart(newUsersRange, canViewUsers)
  const { data: newOrdersData, isLoading: newOrdersLoading } = useNewOrdersChart(newOrdersRange, canViewOrders)
  const { data: revenueData, isLoading: revenueLoading } = useRevenueChart(revenueRange, canViewOrders)
  const { data: orderStatusData, isLoading: orderStatusLoading } = useOrderStatusChart(orderStatusRange, canViewOrders)
  const { data: currenciesData, refetch: refetchCurrencies } = useCurrencies({ active: true, size: 100 })
  const activeCurrencies = currenciesData?.items ?? []
  const baseCurrency = activeCurrencies.find(c => c.isDefault)
  const nonBaseCurrencies = activeCurrencies.filter(c => !c.isDefault)
  const showExchangeRates = activeCurrencies.length > 1

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Stats Loading */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-9 w-24" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Content Loading */}
        <div className="grid gap-6 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <Skeleton key={j} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-10">
          <p className="text-muted-foreground mb-5">{t('error_loading_dashboard')}</p>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching} className="gap-2">
            <RefreshCwIcon className={cn('h-4 w-4', isRefetching && 'animate-spin')} />
            {t('refresh')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  const stats = data?.stats
  const contents = data?.contents

  const statsCards = [
    {
      title: 'unread_notifications',
      value: stats?.unreadNotifications,
      icon: BellIcon,
      href: '/notifications',
      color: 'text-yellow-600 dark:text-yellow-400'
    },
    {
      title: 'orders',
      value: stats?.orders?.total,
      icon: ShoppingCartIcon,
      href: '/orders',
      color: 'text-red-600 dark:text-red-400',
      pendingOrders: stats?.orders?.pending
    },
    {
      title: 'unread_contact_form_messages',
      value: stats?.unreadContactFormMessages,
      icon: MailIcon,
      href: '/user-interactions/contact-messages?read=false',
      color: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'unread_job_applications',
      value: stats?.unreadJobApplications,
      icon: BriefcaseIcon,
      href: '/user-interactions/job-applications?read=false',
      color: 'text-slate-600 dark:text-slate-400'
    },
    {
      title: 'users',
      value: stats?.users?.total,
      icon: UsersIcon,
      href: '/users',
      color: 'text-blue-600 dark:text-blue-400',
      pendingApproval: stats?.users?.pendingApproval
    },
    {
      title: 'groups',
      value: stats?.groups,
      icon: ShieldCheckIcon,
      href: '/users/groups',
      color: 'text-purple-600 dark:text-purple-400'
    },
    {
      title: 'locales',
      value: stats?.locales,
      icon: LanguagesIcon,
      href: '/localization/locales',
      color: 'text-green-600 dark:text-green-400'
    },
    {
      title: 'files',
      value: stats?.files,
      icon: FileIcon,
      href: '/files',
      color: 'text-orange-600 dark:text-orange-400'
    },
    {
      title: 'categories',
      value: stats?.categories?.total,
      icon: FolderTreeIcon,
      href: '/products/categories',
      color: 'text-yellow-600 dark:text-yellow-400',
      badge: stats?.categories
        ? {
            active: stats.categories.active,
            inactive: stats.categories.inactive
          }
        : undefined
    },
    {
      title: 'brands',
      value: stats?.brands,
      icon: TagIcon,
      href: '/products/brands',
      color: 'text-pink-600 dark:text-pink-400'
    },
    {
      title: 'models',
      value: stats?.models,
      icon: TagIcon,
      href: '/products/models',
      color: 'text-rose-600 dark:text-rose-400'
    },
    {
      title: 'products',
      value: stats?.products?.total,
      icon: PackageIcon,
      href: '/products',
      color: 'text-cyan-600 dark:text-cyan-400',
      badge: stats?.products
        ? {
            active: stats.products.active,
            inactive: stats.products.inactive
          }
        : undefined
    },
    {
      title: 'blog_posts',
      value: stats?.blogPosts,
      icon: NewspaperIcon,
      href: '/blog-posts',
      color: 'text-teal-600 dark:text-teal-400'
    },
    {
      title: 'pages',
      value: stats?.productionStages,
      icon: FileTextIcon,
      href: '/pages',
      color: 'text-lime-600 dark:text-lime-400'
    },
    {
      title: 'production_stages',
      value: stats?.productionStages,
      icon: LayersIcon,
      href: '/production-stages',
      color: 'text-emerald-600 dark:text-emerald-400'
    },
    {
      title: 'events',
      value: stats?.events,
      icon: CalendarIcon,
      href: '/events',
      color: 'text-amber-600 dark:text-amber-400'
    },
    {
      title: 'contact_units',
      value: stats?.contactUnits,
      icon: FileClockIcon,
      href: '/contact-units',
      color: 'text-cyan-600 dark:text-cyan-400'
    },
    {
      title: 'photo_galleries',
      value: stats?.photoGalleries,
      icon: ImagesIcon,
      href: '/galleries/photo-galleries',
      color: 'text-violet-600 dark:text-violet-400'
    },
    {
      title: 'slides',
      value: stats?.slides,
      icon: SlidersHorizontalIcon,
      href: '/galleries/slides',
      color: 'text-fuchsia-600 dark:text-fuchsia-400'
    },
    {
      title: 'videos',
      value: stats?.videos,
      icon: VideoIcon,
      href: '/galleries/videos',
      color: 'text-sky-600 dark:text-sky-400'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Stats Section */}
      {stats && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold tracking-tight">{t('statistics')}</h2>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching} className="gap-2">
              <RefreshCwIcon className={cn('h-4 w-4', isRefetching && 'animate-spin')} />
              {t('refresh')}
            </Button>
          </div>
          <div className="xs:grid-cols-1 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {statsCards
              .filter(card => card.value !== undefined)
              .map(card => (
                <div key={card.title} onClick={() => router.push(card.href)} className="cursor-pointer">
                  <Card className="p-0 transition-all hover:shadow-md">
                    <div className="p-3">
                      <div className="mb-2 flex h-8 items-start justify-between overflow-hidden">
                        <h3 className="line-clamp-2 pr-2 text-sm leading-tight font-medium">{t(card.title)}</h3>
                        <card.icon className={cn('h-4 w-4 shrink-0', card.color)} />
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-2xl font-bold">{formatNumber(card.value, locale)}</div>
                        {card.badge && (
                          <div className="flex flex-wrap gap-1">
                            {card.badge.active > 0 && (
                              <Badge variant="default" className="text-xs whitespace-nowrap">
                                {formatNumber(card.badge.active, locale)} {t('active')}
                              </Badge>
                            )}
                            {card.badge.inactive > 0 && (
                              <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                {formatNumber(card.badge.inactive, locale)} {t('inactive')}
                              </Badge>
                            )}
                          </div>
                        )}
                        {'pendingApproval' in card &&
                          card.pendingApproval !== null &&
                          card.pendingApproval !== undefined &&
                          card.pendingApproval > 0 && (
                            <Link href="/users?approved=false" onClick={e => e.stopPropagation()} className="shrink-0">
                              <Badge
                                variant="outline"
                                className="cursor-pointer bg-yellow-100 text-xs whitespace-nowrap text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800">
                                {formatNumber(card.pendingApproval, locale)} {t('pending_approval')}
                              </Badge>
                            </Link>
                          )}
                        {'pendingOrders' in card &&
                          card.pendingOrders !== null &&
                          card.pendingOrders !== undefined &&
                          card.pendingOrders > 0 && (
                            <Link
                              href="/orders?statuses=pending"
                              onClick={e => e.stopPropagation()}
                              className="shrink-0">
                              <Badge
                                variant="outline"
                                className="cursor-pointer bg-orange-100 text-xs whitespace-nowrap text-orange-800 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:hover:bg-orange-800">
                                {formatNumber(card.pendingOrders, locale)} {t('pending')}
                              </Badge>
                            </Link>
                          )}
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Exchange Rates Section */}
      {showExchangeRates && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRightLeftIcon className="h-5 w-5" />
                  {t('exchange_rates')}
                </CardTitle>
                {baseCurrency && (
                  <CardDescription>
                    {t('base_currency')}: {baseCurrency.symbol} {baseCurrency.code} — {baseCurrency.name}
                  </CardDescription>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canViewCurrencies && (
                  <Link href="/currencies">
                    <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                      {t('view_all')}
                    </Badge>
                  </Link>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {nonBaseCurrencies.map(currency => (
                <div key={currency.code} className="flex items-center justify-between rounded-lg border px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm font-medium">1 {baseCurrency?.code ?? '—'}</span>
                    <ArrowRightLeftIcon className="text-muted-foreground h-3.5 w-3.5" />
                    <span className="text-sm font-semibold">
                      {currency.symbol} {currency.code}
                    </span>
                  </div>
                  <span className="text-lg font-bold tabular-nums">
                    {currency.latestRate !== null && currency.latestRate !== undefined
                      ? currency.latestRate.toLocaleString(locale, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4
                        })
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Section */}
      <div>
        <h2 className="mb-4 text-2xl font-bold tracking-tight">{t('charts')}</h2>
        {(canViewUsers || canViewOrders) && (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* New Users */}
            {canViewUsers && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">{t('chart_new_users')}</CardTitle>
                  <RangeSelect value={newUsersRange} onChange={setNewUsersRange} t={t} />
                </CardHeader>
                <CardContent>
                  {newUsersLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : !newUsersData || newUsersData.length === 0 ? (
                    <div className="text-muted-foreground flex h-48 items-center justify-center text-sm">
                      {t('chart_no_data')}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={newUsersData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name={t('chart_registrations')}
                          stroke="#6366f1"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* New Orders */}
            {canViewOrders && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">{t('chart_new_orders')}</CardTitle>
                  <RangeSelect value={newOrdersRange} onChange={setNewOrdersRange} t={t} />
                </CardHeader>
                <CardContent>
                  {newOrdersLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : !newOrdersData || newOrdersData.length === 0 ? (
                    <div className="text-muted-foreground flex h-48 items-center justify-center text-sm">
                      {t('chart_no_data')}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={newOrdersData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name={t('chart_orders')}
                          stroke="#f59e0b"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Revenue */}
            {canViewOrders && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">{t('chart_revenue')}</CardTitle>
                  <RangeSelect value={revenueRange} onChange={setRevenueRange} t={t} />
                </CardHeader>
                <CardContent>
                  {revenueLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : !revenueData || revenueData.length === 0 ? (
                    <div className="text-muted-foreground flex h-48 items-center justify-center text-sm">
                      {t('chart_no_data')}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="total" name={t('chart_revenue')} fill="#10b981" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Order Status Distribution */}
            {canViewOrders && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">{t('chart_order_status_distribution')}</CardTitle>
                  <RangeSelect value={orderStatusRange} onChange={setOrderStatusRange} t={t} />
                </CardHeader>
                <CardContent>
                  {orderStatusLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : !orderStatusData || orderStatusData.length === 0 ? (
                    <div className="text-muted-foreground flex h-48 items-center justify-center text-sm">
                      {t('chart_no_data')}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={orderStatusData.map((d, index) => ({
                            name: t(`order_status_${d.status.toLowerCase()}` as Parameters<typeof t>[0]),
                            value: d.count,
                            fill: PIE_COLORS[index % PIE_COLORS.length]
                          }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          dataKey="value"
                        />
                        <Tooltip />
                        <Legend iconSize={10} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Content Section */}
      {contents && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Latest Products */}
          {contents.latestProducts && contents.latestProducts.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUpIcon className="h-5 w-5" />
                      {t('latest_products')}
                    </CardTitle>
                    <CardDescription>{t('recently_added_products')}</CardDescription>
                  </div>
                  <Link href="/products">
                    <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                      {t('view_all')}
                    </Badge>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col space-y-2">
                  {contents.latestProducts.slice(0, 5).map(product => (
                    <Link key={product.id} href={`/products/${product.id}`}>
                      <div className="hover:bg-accent flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border p-3 transition-colors sm:flex-nowrap">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div className="bg-muted relative h-12 w-12 shrink-0 overflow-hidden rounded-md border">
                            {product.mainPicture ? (
                              <Image
                                src={getResizedPictureUrl(product.mainPicture.path, 90, 90)}
                                alt={product.translations[locale]?.title || product.code}
                                fill
                                className="object-cover"
                                sizes="48px"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <FileIcon className="text-muted-foreground h-6 w-6" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="truncate text-sm leading-none font-medium">
                              {product.translations[locale]?.title || Object.values(product.translations)[0]?.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-muted-foreground text-xs">{product.code}</p>
                              {product.category && (
                                <>
                                  <span className="text-muted-foreground">•</span>
                                  <p className="text-muted-foreground truncate text-xs">
                                    {product.category.translations[locale]?.name ||
                                      Object.values(product.category.translations)[0]?.name}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {product.active ? (
                            <Badge variant="default" className="text-xs whitespace-nowrap">
                              {t('active')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs whitespace-nowrap">
                              {t('inactive')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Latest Users */}
          {contents.latestUsers && contents.latestUsers.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UserIcon className="h-5 w-5" />
                      {t('latest_users')}
                    </CardTitle>
                    <CardDescription>{t('recently_registered_users')}</CardDescription>
                  </div>
                  <Link href="/users">
                    <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                      {t('view_all')}
                    </Badge>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col space-y-2">
                  {contents.latestUsers.slice(0, 5).map(user => (
                    <Link key={user.id} href={`/users/${user.id}`}>
                      <div className="hover:bg-accent flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border p-3 transition-colors sm:flex-nowrap">
                        <div className="flex min-w-0 items-center gap-3">
                          <UserAvatar user={user} size="md" />
                          <div className="min-w-0 space-y-1">
                            <p className="truncate text-sm leading-none font-medium">{user.fullName}</p>
                            <p className="text-muted-foreground text-xs break-all">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1">
                          {user.roles.map(role => (
                            <Badge key={role} variant="secondary" className="text-xs whitespace-nowrap">
                              {t(role)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Latest Blog Posts */}
          {contents.latestBlogPosts && contents.latestBlogPosts.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <NewspaperIcon className="h-5 w-5" />
                      {t('latest_blog_posts')}
                    </CardTitle>
                    <CardDescription>{t('recently_published_posts')}</CardDescription>
                  </div>
                  <Link href="/blog-posts">
                    <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                      {t('view_all')}
                    </Badge>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col space-y-2">
                  {contents.latestBlogPosts.slice(0, 5).map(post => (
                    <Link key={post.id} href={`/blog-posts/${post.id}`}>
                      <div className="hover:bg-accent flex min-w-0 items-center gap-2 rounded-lg border p-3 transition-colors sm:gap-3">
                        <div className="bg-muted relative h-12 w-12 shrink-0 overflow-hidden rounded-md border">
                          {post.mainPicture ? (
                            <Image
                              src={getResizedPictureUrl(post.mainPicture.path, 90, 90)}
                              alt={post.translations[locale]?.title || 'Blog post'}
                              fill
                              className="object-cover"
                              sizes="48px"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <FileIcon className="text-muted-foreground h-6 w-6" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="text-sm leading-tight font-medium wrap-break-word">
                            {post.translations[locale]?.title || Object.values(post.translations)[0]?.title}
                          </p>
                          <p className="text-muted-foreground text-xs wrap-break-word">
                            {formatDateTime(post.createdAt, locale)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Events (Ongoing & Upcoming) */}
          {((contents.ongoingEvents && contents.ongoingEvents.length > 0) ||
            (contents.upcomingEvents && contents.upcomingEvents.length > 0)) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      {t('events')}
                    </CardTitle>
                    <CardDescription>{t('ongoing_and_upcoming_events')}</CardDescription>
                  </div>
                  <Link href="/events">
                    <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                      {t('view_all')}
                    </Badge>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="ongoing" className="flex w-full flex-col gap-2">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="ongoing" className="gap-2">
                      <CalendarClockIcon className="h-4 w-4" />
                      <span className="hidden sm:flex">{t('ongoing')}</span>
                      {contents.ongoingEvents && contents.ongoingEvents.length > 0 && (
                        <Badge variant="success" className="ml-1 text-xs">
                          {contents.ongoingEvents.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="upcoming" className="gap-2">
                      <CalendarDaysIcon className="h-4 w-4" />
                      <span className="hidden sm:flex">{t('upcoming')}</span>
                      {contents.upcomingEvents && contents.upcomingEvents.length > 0 && (
                        <Badge variant="info" className="ml-1 text-xs">
                          {contents.upcomingEvents.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="ongoing">
                    {contents.ongoingEvents && contents.ongoingEvents.length > 0 ? (
                      <div className="flex flex-col space-y-2">
                        {contents.ongoingEvents.slice(0, 5).map(event => (
                          <Link key={event.id} href={`/events/${event.id}`}>
                            <div className="hover:bg-accent flex min-w-0 flex-wrap items-center gap-2 rounded-lg border p-3 transition-colors sm:flex-nowrap sm:gap-3">
                              <div className="bg-muted relative h-12 w-12 shrink-0 overflow-hidden rounded-md border">
                                {event.mainPicture ? (
                                  <Image
                                    src={getResizedPictureUrl(event.mainPicture.path, 90, 90)}
                                    alt={event.translations[locale]?.title || 'Event'}
                                    fill
                                    className="object-cover"
                                    sizes="48px"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center">
                                    <FileIcon className="text-muted-foreground h-6 w-6" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1 space-y-1">
                                <p className="truncate text-sm leading-none font-medium">
                                  {event.translations[locale]?.title || Object.values(event.translations)[0]?.title}
                                </p>
                                <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                                  <span>{formatDateTime(event.beginDate, locale)}</span>
                                  <span>-</span>
                                  <span>{formatDateTime(event.endDate, locale)}</span>
                                </div>
                              </div>
                              <Badge variant="success" className="shrink-0 bg-green-600 text-xs whitespace-nowrap">
                                {t('ongoing')}
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted-foreground flex items-center justify-center py-8 text-sm">
                        {t('no_ongoing_events')}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="upcoming">
                    {contents.upcomingEvents && contents.upcomingEvents.length > 0 ? (
                      <div className="flex flex-col space-y-2">
                        {contents.upcomingEvents.slice(0, 5).map(event => (
                          <Link key={event.id} href={`/events/${event.id}`}>
                            <div className="hover:bg-accent flex min-w-0 flex-wrap items-center gap-2 rounded-lg border p-3 transition-colors sm:flex-nowrap sm:gap-3">
                              <div className="bg-muted relative h-12 w-12 shrink-0 overflow-hidden rounded-md border">
                                {event.mainPicture ? (
                                  <Image
                                    src={getResizedPictureUrl(event.mainPicture.path, 90, 90)}
                                    alt={event.translations[locale]?.title || 'Event'}
                                    fill
                                    className="object-cover"
                                    sizes="48px"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center">
                                    <FileIcon className="text-muted-foreground h-6 w-6" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1 space-y-1">
                                <p className="truncate text-sm leading-none font-medium">
                                  {event.translations[locale]?.title || Object.values(event.translations)[0]?.title}
                                </p>
                                <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                                  <span>{formatDateTime(event.beginDate, locale)}</span>
                                  <span>-</span>
                                  <span>{formatDateTime(event.endDate, locale)}</span>
                                </div>
                              </div>
                              <Badge variant="info" className="shrink-0 text-xs whitespace-nowrap">
                                {t('upcoming')}
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted-foreground flex items-center justify-center py-8 text-sm">
                        {t('no_upcoming_events')}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

export default Dashboard
